import {Injectable} from '@angular/core';
import {DateTime} from 'luxon';
import {TtWorkingTimeEntry} from '../../interfaces/time-tracking/tt-working-time-entry';
import {TtActivityEntry} from '../../interfaces/time-tracking/tt-activity-entry';
import {TtWorkingTimeEntriesTable} from '../../tables/time-tracking/tt-working-time-entries.table';
import {TtActivityEntriesTable} from '../../tables/time-tracking/tt-activity-entries.table';
import {TimeTrackingHelper} from '../../helpers/time-tracking-helper';
import {TtWorkingTimeEntryFactory} from '../../factories/time-tracking/tt-working-time-entry-factory';
import {HashHelper} from '../../helpers/hash-helper';
import {TtWorkingTimeTypesTable} from '../../tables/time-tracking/tt-working-time-types.table';
import {db} from '../../tables/db.service';
import {TtWorkingTimeEntriesResource} from '../../resources/tt-working-time-entries.resource';
import {ErrorMessagesService} from '../core/error-messages.service';
import {firstValueFrom} from 'rxjs';
import {ErrorMessageType} from '../../enums/core/error-message-type';
import {TimeHelper} from '../../helpers/time-helper';
import {WorkingTimeCalculations} from '../../interfaces/time-tracking/working-time-calculations';
import {WorkingTimeCalculationsFactory} from '../../factories/time-tracking/working-time-calculations-factory';
import {EmployeeRostersTable} from '../../tables/employees-roster/employee-rosters.table';
import {EmployeeRoster} from '../../interfaces/employee-rosters/employee-roster';


@Injectable({
  providedIn: 'root'
})
export class TtWorkingTimeEntriesServices {

  constructor(private ttWorkingTimeEntriesTable: TtWorkingTimeEntriesTable,
              private ttWorkingTimeEntriesResource: TtWorkingTimeEntriesResource,
              private employeeRostersTable: EmployeeRostersTable,
              private ttWorkingTimeTypesTable: TtWorkingTimeTypesTable,
              private errorMessagesService: ErrorMessagesService,
              private ttActivityEntriesTable: TtActivityEntriesTable) {
  }

  /**
   *
   * @param query
   */
  public async loadTtWorkingTimeEntries(query: {
    dateFrom?: string,
    dateTo?: string,
    employeeId?: number
  } = {}): Promise<TtWorkingTimeEntry[]> {
    try {
      const ttWorkingTimeEntries = await firstValueFrom(this.ttWorkingTimeEntriesResource.load(query));
      await this.ttWorkingTimeEntriesTable.removeByPeriod(DateTime.fromISO(query.dateFrom), DateTime.fromISO(query.dateTo));
      await this.ttWorkingTimeEntriesTable.addMany(ttWorkingTimeEntries);
      // Calculate only the current day
      await this.calculateTtWorkingTimeEntries();
      return ttWorkingTimeEntries;
    }
    catch (e) {
      await this.errorMessagesService.showErrorMessage(ErrorMessageType.Connection);
      console.error(e);
      throw e;
    }
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public async calculateWorkingTime(dateFrom: string | DateTime | Date, dateTo: string | DateTime | Date): Promise<number> {
    const today = DateTime.local().toISODate();
    dateFrom = TimeHelper.getDateString(dateFrom);
    dateTo = TimeHelper.getDateString(dateTo);

    if(dateFrom > today) {
      return 0;
    }
    if(dateTo > today) {
      dateTo = today;
    }

    const ttWorkingTimeEntries = await this.ttWorkingTimeEntriesTable.getByPeriod(dateFrom, dateTo);

    return ttWorkingTimeEntries
      .filter(_ttWorkingTimeEntry => _ttWorkingTimeEntry.ttWorkingTimeType.isInCalculation)
      .reduce((_duration, _ttWorkingTimeEntry) => {
      return _duration + _ttWorkingTimeEntry.duration;
    }, 0);
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public async calculateTtWorkingTimeEntries(dateFrom: string | DateTime | Date = new Date(), dateTo: string | DateTime | Date = new Date()): Promise<TtWorkingTimeEntry[]> {
    dateFrom = TimeHelper.getDateTime(dateFrom);
    dateTo = TimeHelper.getDateTime(dateTo);

    let ttActivityEntries: TtActivityEntry[];

    if(dateFrom) {
      ttActivityEntries = await this.ttActivityEntriesTable.getByPeriod(dateFrom as DateTime, dateTo as DateTime);
    }
    else {
      ttActivityEntries = await this.ttActivityEntriesTable.getAll();
    }

    const ttWorkingTimeEntries: TtWorkingTimeEntry[] = [];
    let currentTtWorkingTimeEntry: TtWorkingTimeEntry = null;

    for(const ttActivityEntry of ttActivityEntries) {
      try {
        if(ttActivityEntry.ttActivityType.isStandby) {
          const ttWorkingTimeEntry = await this.createFromTtActivityEntry(ttActivityEntry);
          ttWorkingTimeEntries.push(ttWorkingTimeEntry);
          continue;
        }

        if(currentTtWorkingTimeEntry) {
          const timeFrom = TimeTrackingHelper.getTimeFrom(ttActivityEntry);
          const timeTo = DateTime.fromISO(currentTtWorkingTimeEntry.timeTo);

          if(timeTo.hasSame(timeFrom, 'minute') && currentTtWorkingTimeEntry.ttWorkingTimeTypeId === ttActivityEntry.ttWorkingTimeTypeId) {
            currentTtWorkingTimeEntry.timeTo = ttActivityEntry.timeTo;
            currentTtWorkingTimeEntry.duration = DateTime.fromISO(currentTtWorkingTimeEntry.timeTo)
              .diff(DateTime.fromISO(currentTtWorkingTimeEntry.timeFrom), 'minutes').as('minutes');
            continue;
          }
        }

        currentTtWorkingTimeEntry = await this.createFromTtActivityEntry(ttActivityEntry);
        ttWorkingTimeEntries.push(currentTtWorkingTimeEntry);
      }
      catch(e) {
        console.log(e);
        break;
      }
    }
    try {
      if(dateFrom) {
        await this.ttWorkingTimeEntriesTable.removeByDate(dateFrom);
      }
      else {
        await this.ttWorkingTimeEntriesTable.clear();
      }
      await this.ttWorkingTimeEntriesTable.addMany(ttWorkingTimeEntries);
    }
    catch (e) {
      await this.errorMessagesService.showErrorMessage(ErrorMessageType.Storage);
    }

    return ttWorkingTimeEntries;
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public async getWorkingTimeCalculations(dateFrom: string | DateTime | Date, dateTo: string | DateTime | Date): Promise<WorkingTimeCalculations> {
    dateFrom = TimeHelper.getDateString(dateFrom);
    dateTo = TimeHelper.getDateString(dateTo);

    const workingTimeCalculations = WorkingTimeCalculationsFactory.new();

    const ttWorkingTimeEntries: TtWorkingTimeEntry[] = await this.ttWorkingTimeEntriesTable.getByPeriod(dateFrom, dateTo);
    const employeeRosters: EmployeeRoster[] = await this.employeeRostersTable.getByPeriod(dateFrom, dateTo);

    const today = DateTime.local().toISODate();

    for(const ttWorkingTimeEntry of ttWorkingTimeEntries) {
      if(ttWorkingTimeEntry.ttWorkingTimeType.isInCalculation) {
        const date = TimeHelper.getDateString(ttWorkingTimeEntry.timeFrom);
        if(date < today) {
          workingTimeCalculations.workingTime += ttWorkingTimeEntry.duration;
        }
        workingTimeCalculations.totalWorkingTime += ttWorkingTimeEntry.duration;
      }
    }

    for(const employeeRoster of employeeRosters) {
      const date = TimeHelper.getDateString(employeeRoster.date);
      workingTimeCalculations.plannedWorkingTime += employeeRoster.workingMinutes;

      if(date >= today) {
        workingTimeCalculations.workingTimeExtrapolated += employeeRoster.workingMinutes;
      }
    }

    workingTimeCalculations.workingTimeExtrapolated += workingTimeCalculations.workingTime;

    workingTimeCalculations.plannedWorkingTimeDifference =
      workingTimeCalculations.workingTime - workingTimeCalculations.plannedWorkingTime;

    workingTimeCalculations.workingTimeExtrapolatedDifference =
      workingTimeCalculations.workingTimeExtrapolated - workingTimeCalculations.plannedWorkingTime;

    return workingTimeCalculations;
  }

  /**
   *
   * @param ttActivityEntry
   * @private
   */
  private async createFromTtActivityEntry(ttActivityEntry: TtActivityEntry): Promise<TtWorkingTimeEntry> {
    const ttWorkingTimeEntry = TtWorkingTimeEntryFactory.new();
    const timeFrom = TimeTrackingHelper.getTimeFrom(ttActivityEntry);

    const ttWorkingTimeTypeId = ttActivityEntry.ttWorkingTimeTypeId;
    const ttWorkingTimeType = await this.ttWorkingTimeTypesTable.get(ttWorkingTimeTypeId);

    if(!ttWorkingTimeType) {
      throw new Error('No ttWorkingTimeTypes found');
    }

    ttWorkingTimeEntry.id = HashHelper.getGuid();
    ttWorkingTimeEntry.timeFrom = timeFrom.toISO();
    ttWorkingTimeEntry.timeTo = ttActivityEntry.timeTo;
    ttWorkingTimeEntry.ttWorkingTimeType = ttWorkingTimeType;
    ttWorkingTimeEntry.ttWorkingTimeTypeId = ttWorkingTimeType.id;
    ttWorkingTimeEntry.employeeId = ttActivityEntry.employeeId;
    ttWorkingTimeEntry.duration = DateTime.fromISO(ttActivityEntry.timeTo)
      .diff(timeFrom, 'minutes').as('minutes');
    ttWorkingTimeEntry.employee = ttActivityEntry.employee;
    return ttWorkingTimeEntry;
  }

}
